import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { mockWebinars } from '../data/webinarData';
import WebinarRegistrationModal from '../components/Webinars/WebinarRegistrationModal';
import WebinarSidebarCalendar from '../components/Webinars/WebinarSidebarCalendar';
import StreamSearchHeader from '../components/Live/StreamSearchHeader';

const {
  FiVideo, FiClock, FiCalendar, FiArrowRight, FiZap,
  FiMapPin, FiBriefcase, FiDollarSign, FiCheckCircle, FiRadio,
} = FiIcons;

const STORAGE_KEY = 'ofl_registered_webinars';

const Webinars = () => {
  const navigate = useNavigate();
  const [registeredIds, setRegisteredIds] = useState(() =>
    JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
  );
  const [selectedWebinar, setSelectedWebinar] = useState(null);
  const [activeCategory, setActiveCategory]   = useState('All');
  const [filteredWebinars, setFilteredWebinars] = useState(mockWebinars);
  const [filters, setFilters] = useState({ location: '', workPlace: '', familyName: '' });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(registeredIds));
  }, [registeredIds]);

  const handleRegistrationSuccess = (id) => {
    setRegisteredIds(prev => prev.includes(id) ? prev : [...prev, id]);
  };

  /**
   * Memoised with useCallback so it has a stable reference and can safely
   * be listed as a dependency of the category-change effect without
   * triggering an infinite re-render loop.
   */
  const applyFilters = useCallback((term = '', cat = 'All', activeFilters = {}) => {
    const lower = term.toLowerCase();
    setFilteredWebinars(
      mockWebinars.filter(w => {
        const matchCat    = cat === 'All' || w.category === cat;
        const matchSearch = !lower || [w.title, w.description, w.speaker, w.deceasedName, w.familyName]
          .some(f => typeof f === 'string' && f.toLowerCase().includes(lower));
        const matchLoc    = !activeFilters.location  || (w.location  ?? '').toLowerCase().includes(activeFilters.location.toLowerCase());
        const matchWork   = !activeFilters.workPlace || (w.workPlace ?? '').toLowerCase().includes(activeFilters.workPlace.toLowerCase());
        const matchFam    = !activeFilters.familyName || (w.familyName ?? '').toLowerCase().includes(activeFilters.familyName.toLowerCase());
        return matchCat && matchSearch && matchLoc && matchWork && matchFam;
      })
    );
  }, []);

  const handleSearch = useCallback(
    (term, activeFilters) => applyFilters(term, activeCategory, activeFilters),
    [applyFilters, activeCategory]
  );

  /* Re-filter whenever the category pill changes */
  useEffect(() => {
    applyFilters('', activeCategory, filters);
  }, [activeCategory, applyFilters, filters]);

  const categories = ['All', 'Writing', 'Legal', 'Wellness', 'Planning'];

  return (
    <div className="pt-32 min-h-screen bg-brand-light pb-20">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl mx-auto text-center mb-16 md:mb-24">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 bg-accent-gold/10 text-accent-gold px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.2em] mb-8 border border-accent-gold/20"
          >
            <SafeIcon icon={FiVideo} /> Legacy Knowledge Base
          </motion.div>
          <h1 className="text-4xl md:text-7xl font-serif text-accent-navy mb-8 leading-tight">
            Webinars & Workshops
          </h1>
          <p className="text-lg md:text-xl text-text-muted font-light max-w-2xl mx-auto leading-relaxed mb-12">
            Join expert-led sessions on legacy preservation, grief support, and estate planning.
          </p>
        </div>

        <StreamSearchHeader onSearch={handleSearch} filters={filters} setFilters={setFilters} />

        <div className="flex flex-col lg:flex-row gap-12">
          <div className="lg:w-2/3 space-y-12">
            {/* Category Pills */}
            <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-6 py-2.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all border-2 shrink-0 ${
                    activeCategory === cat
                      ? 'bg-accent-navy border-accent-navy text-white shadow-lg'
                      : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-10">
              <AnimatePresence mode="popLayout">
                {filteredWebinars.length > 0
                  ? filteredWebinars.map((webinar, idx) => {
                      const isRegistered = registeredIds.includes(webinar.id);
                      const isFree       = webinar.monetization?.isFree !== false;
                      return (
                        <motion.div
                          key={webinar.id}
                          layout
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ delay: idx * 0.05 }}
                          className="bg-white rounded-[40px] overflow-hidden shadow-sm border border-brand-dark group hover:shadow-2xl transition-all duration-500 flex flex-col md:flex-row"
                        >
                          {/* Image */}
                          <div className="relative w-full md:w-80 h-64 md:h-auto overflow-hidden shrink-0">
                            <img
                              src={webinar.image}
                              alt=""
                              className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-black/80 via-black/20 to-transparent" />
                            <div className="absolute top-6 left-6 flex flex-col gap-2">
                              {webinar.isLive && (
                                <span className="bg-red-600 text-white px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg animate-pulse">
                                  <span className="h-1.5 w-1.5 rounded-full bg-white" /> Live Now
                                </span>
                              )}
                              <span
                                className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg ${
                                  isFree ? 'bg-green-600 text-white' : 'bg-amber-500 text-white'
                                }`}
                              >
                                <SafeIcon
                                  icon={isFree ? FiCheckCircle : FiDollarSign}
                                  className="w-3 h-3"
                                />
                                {isFree
                                  ? 'Free'
                                  : `${webinar.monetization?.currency ?? 'USD'} ${webinar.monetization?.price ?? 0}`}
                              </span>
                              {webinar.platformConfig?.platform === 'native' && (
                                <span className="bg-red-900/80 text-white px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest flex items-center gap-2">
                                  <SafeIcon icon={FiRadio} className="w-3 h-3" /> RTMP
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Content */}
                          <div className="p-8 md:p-10 flex-grow flex flex-col">
                            <div className="flex flex-wrap gap-2 mb-4">
                              {webinar.location && (
                                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-brand-light text-accent-navy text-[8px] font-black uppercase tracking-tighter rounded-full border border-brand-dark">
                                  <SafeIcon icon={FiMapPin} className="w-2.5 h-2.5 text-accent-gold" />
                                  {webinar.location}
                                </span>
                              )}
                              {webinar.workPlace && (
                                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-brand-light text-accent-navy text-[8px] font-black uppercase tracking-tighter rounded-full border border-brand-dark">
                                  <SafeIcon icon={FiBriefcase} className="w-2.5 h-2.5 text-accent-gold" />
                                  {webinar.workPlace}
                                </span>
                              )}
                            </div>

                            <p className="text-accent-gold text-[9px] font-black uppercase tracking-widest mb-2">
                              {webinar.seriesTitle || 'Special Session'}
                            </p>
                            <h3 className="text-2xl font-serif text-accent-navy mb-4 leading-tight group-hover:text-accent-gold transition-colors">
                              {webinar.title}
                            </h3>
                            <p className="text-text-muted font-light leading-relaxed mb-8 text-sm line-clamp-2">
                              "{webinar.description}"
                            </p>

                            <div className="grid grid-cols-2 gap-4 mb-8 text-[10px] font-black uppercase tracking-widest text-text-muted">
                              <div className="flex items-center gap-2">
                                <SafeIcon icon={FiCalendar} className="text-accent-gold" />
                                {webinar.date || '—'}
                              </div>
                              <div className="flex items-center gap-2">
                                <SafeIcon icon={FiClock} className="text-accent-gold" />
                                {webinar.time || '—'}
                              </div>
                            </div>

                            {!isFree && (webinar.monetization?.discountPercent ?? 0) > 0 && !isRegistered && (
                              <div className="mb-4 px-4 py-2 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700 font-bold">
                                🎟 Early bird: {webinar.monetization.discountPercent}% off — limited time
                              </div>
                            )}

                            <div className="mt-auto pt-6 border-t border-brand-light flex flex-wrap gap-3">
                              {isRegistered ? (
                                <button
                                  onClick={() => navigate(`/webinar/${webinar.id}`)}
                                  className="flex-grow btn-accent py-4 text-xs flex items-center justify-center gap-3 shadow-xl group"
                                >
                                  <SafeIcon icon={FiZap} className="group-hover:animate-pulse" />
                                  Enter Waiting Lobby
                                </button>
                              ) : (
                                <button
                                  onClick={() => setSelectedWebinar(webinar)}
                                  className="flex-grow btn-primary py-4 text-xs flex items-center justify-center gap-3 shadow-xl group"
                                >
                                  {isFree
                                    ? 'Register Free'
                                    : `Register — ${webinar.monetization?.currency ?? 'USD'} ${webinar.monetization?.price ?? 0}`}
                                  <SafeIcon
                                    icon={FiArrowRight}
                                    className="group-hover:translate-x-1 transition-transform"
                                  />
                                </button>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      );
                    })
                  : (
                    <div className="py-20 text-center bg-white rounded-[40px] border-2 border-dashed border-brand-dark">
                      <h3 className="text-2xl font-serif text-accent-navy mb-2">No sessions matched</h3>
                      <p className="text-text-muted max-w-sm mx-auto">Try a different search or clear filters.</p>
                    </div>
                  )}
              </AnimatePresence>
            </div>
          </div>

          <div className="lg:w-1/3">
            <div className="sticky top-32">
              <WebinarSidebarCalendar webinars={mockWebinars} registeredIds={registeredIds} />
            </div>
          </div>
        </div>
      </div>

      <WebinarRegistrationModal
        isOpen={!!selectedWebinar}
        onClose={() => setSelectedWebinar(null)}
        webinar={selectedWebinar}
        onRegistered={handleRegistrationSuccess}
      />
    </div>
  );
};

export default Webinars;